import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { backgroundReadContext } from '@app/core/http/silent-background-read.context';
import { ReadOptions } from '@app/domain/models/read-options.model';
import {
  BusinessUnitListQuery,
  BusinessUnitDirectoryRepository,
} from '@app/domain/ports/tpv/business-unit.repository';
import { TpvBusinessUnit } from '@app/domain/models/tpv/business-unit.model';
import { TpvPage } from '@app/domain/models/tpv/tpv-page.model';
import { BusinessUnitDto } from '@app/data/dtos/tpv/business-unit.dto';
import { mapBusinessUnit } from '@app/data/mappers/tpv/business-unit.mapper';
import { toTpvPage } from '@app/data/mappers/tpv/tpv-page.mapper';
import { tpvUrl } from '@app/data/tpv/tpv-api.config';
import { TpvEnvelope } from '@app/data/tpv/tpv-envelope';
import { toHttpParams } from './tpv-http.util';

/** `GET /api/v1/tpv/business-units/`. Auth vía interceptores estándar. */
@Injectable()
export class BusinessUnitRemoteDataSource extends BusinessUnitDirectoryRepository {
  private readonly http = inject(HttpClient);

  override getBusinessUnits(
    query: BusinessUnitListQuery = {},
    options?: ReadOptions,
  ): Observable<TpvPage<TpvBusinessUnit>> {
    // Workaround micro TPV: `get_all=true` devuelve el set completo ya scopeado al
    // usuario. No se envía page/limit (la paginación la arma el micro y deja fuera
    // unidades); los filtros sí siguen aplicando server-side. El conteo real es
    // `data.length`, NO el `total` del body (viene inflado). Ver toTpvPage(getAll).
    const params = toHttpParams({
      get_all: true,
      // Búsqueda server-side (OR entre nombre, razón social, empresa y RFC).
      // Contrato 2026-09-29: `?search=` sustituye a `?name=` para la caja de
      // texto — el back miraba solo `name` y descartaba filas cuyo RFC coincidía.
      search: query.search,
      name: query.merchantName,
      company_name: query.companyName,
      id_company: query.idCompany,
      id_unit: query.idUnit,
      entity: query.entity,
      // Array → CSV automático en toHttpParams (String(['A','B']) === 'A,B').
      status: query.status,
    });

    // `context` solo lleva marca cuando el llamador la pidió. La pantalla de
    // Unidades lee esta MISMA url sin ella, y su 5xx sigue avisando.
    return this.http
      .get<TpvEnvelope<unknown>>(tpvUrl('business-units/'), {
        params,
        context: backgroundReadContext(options),
      })
      .pipe(
        map((raw) =>
          toTpvPage<BusinessUnitDto, TpvBusinessUnit>(raw, mapBusinessUnit, {
            page: 1,
            limit: 0,
            getAll: true,
          }),
        ),
      );
  }
}
